import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Send,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Check,
  FileText,
  ShoppingBag,
  Workflow,
  X,
} from 'lucide-react';

type ActivePicker = 'template' | 'product' | 'flow' | null;

interface ComposeBarProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isSending: boolean;
  sendError: string | null;
  sendSuccess: boolean;
  isOutsideWindow: boolean;
  showTemplateSelector: boolean;
  onShowTemplateSelector: (show: boolean) => void;
  templates: Array<{ slug: string; template: { bodyText: string } }>;
  selectedTemplate: string;
  onSelectTemplate: (slug: string) => void;
  onSendTemplate: () => void;
  // Product sending
  products?: Array<{ id: string; name: string; priceCents: number }>;
  onSendProduct?: (productId: string) => void;
  onSendProductList?: (productIds: string[]) => void;
  // Flow sending
  flows?: Array<{ id: string; name: string }>;
  onSendFlow?: (flowId: string) => void;
}

export function ComposeBar({
  value,
  onChange,
  onSend,
  isSending,
  sendError,
  sendSuccess,
  isOutsideWindow,
  showTemplateSelector,
  onShowTemplateSelector,
  templates,
  selectedTemplate,
  onSelectTemplate,
  onSendTemplate,
  products = [],
  onSendProduct,
  onSendProductList,
  flows = [],
  onSendFlow,
}: ComposeBarProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [activePicker, setActivePicker] = useState<ActivePicker>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [selectedFlowId, setSelectedFlowId] = useState<string>('');

  const handleInput = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend]
  );

  const togglePicker = useCallback((picker: ActivePicker) => {
    setActivePicker((prev) => (prev === picker ? null : picker));
    // Reset selections when switching pickers
    setSelectedProductIds([]);
    setSelectedFlowId('');
    // Also sync template selector state
    if (picker === 'template') {
      onShowTemplateSelector(true);
    } else {
      onShowTemplateSelector(false);
      onSelectTemplate('');
    }
  }, [onShowTemplateSelector, onSelectTemplate]);

  const closePicker = useCallback(() => {
    setActivePicker(null);
    setSelectedProductIds([]);
    setSelectedFlowId('');
    onShowTemplateSelector(false);
    onSelectTemplate('');
  }, [onShowTemplateSelector, onSelectTemplate]);

  const handleProductToggle = useCallback((productId: string) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  }, []);

  const handleSendProducts = useCallback(() => {
    if (selectedProductIds.length === 0) return;
    if (selectedProductIds.length === 1 && onSendProduct) {
      onSendProduct(selectedProductIds[0]);
    } else if (onSendProductList) {
      onSendProductList(selectedProductIds);
    }
    setSelectedProductIds([]);
    setActivePicker(null);
  }, [selectedProductIds, onSendProduct, onSendProductList]);

  const handleSendFlow = useCallback(() => {
    if (!selectedFlowId || !onSendFlow) return;
    onSendFlow(selectedFlowId);
    setSelectedFlowId('');
    setActivePicker(null);
  }, [selectedFlowId, onSendFlow]);

  const formatPrice = (cents: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);

  // Shared picker UI for template/product/flow
  const renderPickerContent = () => {
    if (!activePicker) return null;

    if (activePicker === 'template') {
      return (
        <div className="flex gap-2 items-end">
          <select
            value={selectedTemplate}
            onChange={(e) => onSelectTemplate(e.target.value)}
            disabled={isSending}
            className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">{t('conversations.compose.selectTemplate')}</option>
            {templates.map((tpl) => (
              <option key={tpl.slug} value={tpl.slug}>{tpl.slug}</option>
            ))}
          </select>
          <Button
            size="sm"
            onClick={onSendTemplate}
            disabled={!selectedTemplate || isSending}
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={closePicker} disabled={isSending}>
            {t('common.cancel')}
          </Button>
        </div>
      );
    }

    if (activePicker === 'product') {
      return (
        <div className="space-y-2">
          {products.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('conversations.compose.selectProduct')}</p>
          ) : (
            <div className="max-h-32 overflow-y-auto space-y-1">
              {products.map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedProductIds.includes(p.id)}
                    onChange={() => handleProductToggle(p.id)}
                    className="h-3.5 w-3.5 rounded border-input"
                  />
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{formatPrice(p.priceCents)}</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={closePicker} disabled={isSending}>
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={handleSendProducts}
              disabled={selectedProductIds.length === 0 || isSending}
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Send className="h-4 w-4 mr-1" />
              )}
              {t('conversations.compose.sendProduct')}
              {selectedProductIds.length > 1 && ` (${selectedProductIds.length})`}
            </Button>
          </div>
        </div>
      );
    }

    if (activePicker === 'flow') {
      return (
        <div className="flex gap-2 items-end">
          <select
            value={selectedFlowId}
            onChange={(e) => setSelectedFlowId(e.target.value)}
            disabled={isSending}
            className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">{t('conversations.compose.selectFlow')}</option>
            {flows.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <Button
            size="sm"
            onClick={handleSendFlow}
            disabled={!selectedFlowId || isSending}
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={closePicker} disabled={isSending}>
            {t('common.cancel')}
          </Button>
        </div>
      );
    }

    return null;
  };

  // Icon toolbar for attachment types
  const renderIconToolbar = () => (
    <div className="flex items-center gap-1 mb-1">
      <button
        type="button"
        onClick={() => togglePicker('template')}
        className={`p-1.5 rounded-md transition-colors ${activePicker === 'template' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
        title={t('conversations.compose.sendTemplate')}
      >
        <FileText className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => togglePicker('product')}
        className={`p-1.5 rounded-md transition-colors ${activePicker === 'product' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
        title={t('conversations.compose.sendProduct')}
      >
        <ShoppingBag className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => togglePicker('flow')}
        className={`p-1.5 rounded-md transition-colors ${activePicker === 'flow' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
        title={t('conversations.compose.sendFlow')}
      >
        <Workflow className="h-4 w-4" />
      </button>
      {activePicker && (
        <button
          type="button"
          onClick={closePicker}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted ml-auto"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <div className="border-t bg-background px-4 py-2 shrink-0">
      {/* Feedback messages */}
      {sendError && (
        <div className="flex items-center gap-2 text-red-600 text-xs mb-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {sendError}
        </div>
      )}
      {sendSuccess && (
        <div className="flex items-center gap-2 text-emerald-600 text-xs mb-2">
          <Check className="h-3.5 w-3.5 shrink-0" />
          {t('conversations.compose.sendSuccess')}
        </div>
      )}

      {isOutsideWindow ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {t('conversations.compose.outsideWindow')}
          </div>
          {renderIconToolbar()}
          {activePicker ? (
            renderPickerContent()
          ) : (
            <Button
              className="w-full"
              size="sm"
              onClick={() => togglePicker('template')}
            >
              <FileText className="h-4 w-4 mr-2" />
              {t('conversations.compose.sendTemplate')}
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          {renderIconToolbar()}
          {activePicker && (
            <div className="mb-2">
              {renderPickerContent()}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => {
                if (e.target.value.length <= 4096) onChange(e.target.value);
                handleInput();
              }}
              onKeyDown={handleKeyDown}
              placeholder={t('conversations.compose.placeholder')}
              rows={1}
              disabled={isSending}
              className="flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[38px] max-h-[120px]"
            />
            <Button
              size="icon"
              className="h-[38px] w-[38px] shrink-0"
              onClick={onSend}
              disabled={!value.trim() || isSending}
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
